import { Component, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Performance } from '@angular/fire/performance';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TenantUsersDto } from '@buspro/shared/dtos';
import { TenantAuthService, WEB_CONFIG, WebAppEnvironment } from '@buspro/web/core';
import { ThemeService } from '@buspro/web/core';
import { PrintService } from '@buspro/web/tenants/printing';
// import { AngularQueryDevtools } from '@tanstack/angular-query-devtools-experimental';
import { NgxAuthService } from '@corelabs/ng-auth';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { injectIsFetching } from '@tanstack/angular-query-experimental';
import { NGXLogger } from 'ngx-logger';
import { Observable, filter, map, mergeMap, tap } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-root',
  standalone: true,
  providers: [],
  imports: [LoadingBarModule, RouterOutlet],
  template: `
    <main>
      <ngx-loading-bar color="red"></ngx-loading-bar>
           <!-- <buspro-token-refresh></buspro-token-refresh> TODO: Needs fixing to work well -->
      @if(!printingActive()) {
      <router-outlet></router-outlet>
      }@else {
      <router-outlet name="print"></router-outlet>
      }
    </main>
  `,
})
export class AppComponent implements OnInit {
  // Injecting ThemeService here ensures the theme (data-theme on <html>) is applied
  // for every route—including auth pages—not only the workspace layout.
  private themeService = inject(ThemeService);
  private authService = inject(TenantAuthService);
  private logger = inject(NGXLogger);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private titleService = inject(Title);
  private performance = inject(Performance);
  private ngxAuthService = inject(NgxAuthService);
  private printingService = inject(PrintService);

  // For Global background Fetching State
  isFetching = injectIsFetching();

  printingActive = this.printingService.isPrinting;

  protected readonly environment = inject<WebAppEnvironment>(WEB_CONFIG);

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  authUser = toSignal(this.authUser$, { initialValue: null });

  constructor() {
    // Auth initialization is now handled by APP_INITIALIZER
    // No need to call initialize() here anymore

    // Keep state subscription for debugging if needed
    this.ngxAuthService.state.subscribe((value) => {
      console.log('Auth service status', value);
    });
  }

  ngOnInit(): void {
    this.initializeAuth();
    this.setupTitleUpdates();

    this.performance.dataCollectionEnabled = true;
    this.performance.instrumentationEnabled = true;
    this.performance.app.automaticDataCollectionEnabled = true;
  }

  private initializeAuth(): void {
    this.authUser$.pipe(untilDestroyed(this)).subscribe(
      (user: TenantUsersDto | null) => this.logger.debug('Current user:', user),
      (error: Error) => this.logger.error('Auth error:', error),
    );
  }

  private setupTitleUpdates(): void {
    this.router.events
      .pipe(
        tap((event) => {
          if (event instanceof NavigationEnd) {
            console.log('NavigationEnd:', event);
            const _currentRoute = this.router.url;
            // if (
            //   currentRoute.startsWith('/parcels(print:print/parcel-receipt/') ||
            //   currentRoute.startsWith('/parcels(print:print/parcel-sticker')
            // ) {
            //   this.printingActive.set(true);
            // } else {
            //   this.printingActive.set(false);
            // }
          }
        }),
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getChildRoute(this.activatedRoute)),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        untilDestroyed(this),
      )
      .subscribe(
        (data) => {
          this.titleService.setTitle(`BusPro | ${data['title']}`);
          // this.authService.setInterruptedUrl(this.router.url);
        },
        (error) => this.logger.error('Title update error:', error),
      );
  }

  private getChildRoute(activatedRoute: ActivatedRoute): ActivatedRoute {
    let currentRoute = activatedRoute;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }
    return currentRoute;
  }
}
